/**
 * Color System for Putevi Republike Srpske ERP
 *
 * Brand Colors: #CC3433 (Red), #340067 (Purple), Gray
 * Primary UI: Professional Blue
 *
 * Two-tier token system:
 * 1. Primitive tokens (base colors) - defined once, never used directly
 * 2. Semantic tokens (functional colors) - reference primitives, used in components
 */

// ============================================
// PRIMITIVE TOKENS (Base Palette)
// ============================================
export const colors = {
  // Neutrals (Grays) - Professional government palette
  neutral: {
    50: '#f9fafb',   // Lightest
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#808080',  // Brand gray
    600: '#6b7280',
    700: '#4b5563',
    800: '#374151',
    900: '#1f2937',
    950: '#111827',  // Darkest
  },

  // Primary UI Color (Professional Blue - Government Standard)
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',  // Main UI color
    900: '#1e3a8a',
  },

  // Brand Purple (Identity - Used Sparingly)
  brandPurple: {
    base: '#340067',     // Official brand color
    light: '#5b18a8',    // Lighter variant
    lighter: '#7c2fd4',  // Even lighter
    dark: '#260050',     // Darker variant
    tint: '#f4ebff',     // Very light tint for backgrounds
  },

  // Brand Red (Action/Accent)
  brandRed: {
    base: '#CC3433',     // Official brand color
    light: '#e74f4e',    // Lighter variant
   lighter: '#ff6b6a',  // Even lighter
    dark: '#b91c1c',     // Darker variant
    tint: '#fef2f2',     // Very light tint for backgrounds
  },

  // Success (Green)
  green: {
    50: '#f0fdf4',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },

  // Warning (Amber)
  amber: {
    50: '#fffbeb',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },

  // Info (Cyan)
  cyan: {
    50: '#ecfeff',
    500: '#06b6d4',
    600: '#0891b2',
    700: '#0e7490',
  },

  // Pure colors
  white: '#ffffff',
  black: '#000000',
} as const;

// ============================================
// SEMANTIC TOKENS (Functional Mapping)
// ============================================
export const semanticColors = {
  // Primary UI color (Professional Blue)
  primary: {
    main: colors.blue[800],      // #1e40af
    light: colors.blue[600],     // #2563eb
    dark: colors.blue[900],      // #1e3a8a
    contrastText: colors.white,
  },

  // Secondary accent (Brand Red - Important Actions)
  secondary: {
    main: colors.brandRed.base,   // #CC3433 (Brand color)
    light: colors.brandRed.light, // #e74f4e
    dark: colors.brandRed.dark,   // #b91c1c
    contrastText: colors.white,
  },

  // Brand accent (Purple - Identity)
  brandAccent: {
    main: colors.brandPurple.base,    // #340067 (Brand color)
    light: colors.brandPurple.lighter, // #7c2fd4
    dark: colors.brandPurple.dark,    // #260050
    contrastText: colors.white,
  },

  // Status colors
  success: {
    main: colors.green[600],
    light: colors.green[500],
    dark: colors.green[700],
    contrastText: colors.white,
  },

  warning: {
    main: colors.amber[600],
    light: colors.amber[500],
    dark: colors.amber[700],
    contrastText: colors.white,
  },

  error: {
    main: colors.brandRed.base,   // #CC3433 (Brand color)
    light: colors.brandRed.light, // #e74f4e
    dark: colors.brandRed.dark,   // #b91c1c
    contrastText: colors.white,
  },

  info: {
    main: colors.blue[500],      // #3b82f6
    light: colors.blue[300],     // #93c5fd
    dark: colors.blue[700],      // #1d4ed8
    contrastText: colors.white,
  },

  // Background colors
  background: {
    default: colors.neutral[100],  // #f3f4f6
    paper: colors.white,
    elevated: colors.white,
  },

  // Text colors
  text: {
    primary: colors.neutral[900],    // #1f2937
    secondary: colors.neutral[600],  // #6b7280
    disabled: colors.neutral[400],   // #9ca3af
  },

  // Divider and borders
  divider: colors.neutral[200],      // #e5e7eb
  border: {
    light: colors.neutral[200],
    main: colors.neutral[300],
    dark: colors.neutral[400],
  },

  // Action states
  action: {
    active: colors.blue[800],
    hover: colors.neutral[50],
    selected: colors.blue[50],
    disabled: colors.neutral[300],
    disabledBackground: colors.neutral[100],
    focus: colors.blue[100],
  },
} as const;

